<template>
  <div class="keyword-movements">
    <div class="keyword-movements__header">
      <div class="keyword-movements__title">{{keywordMovementTitle}}</div>
      <div class="keyword-movements__rank-zone">Rank Zone {{cellName}}</div>
      <div class="keyword-movements__export-button"
           v-if="isReport && isDataFetched"
           @click="exportCsv()">
        <base-material-design-icon icon="download" />
      </div>
    </div>
    <div class="keyword-movements__table" v-if="isDataFetched">
      <base-table-collapsible class="keyword-movements__table"
                              :config="config"
                              :webPropertyGroupId="webPropertyGroupId"
                              :data="tableData"
                              :shifted="false"
                              :show-summary="false"
                              :is-keywords-modal="true"
                              ref="table">

      </base-table-collapsible>
      <footer class="keyword-movements__footer">
        <div class="keyword-movements__footer-title">Total</div>
        <div class="keyword-movements__footer-msv">{{total.msv | commaDividedNumber}}</div>
        <div class="keyword-movements__footer-keywords">{{total.keywords | commaDividedNumber}}</div>
      </footer>
    </div>
    <div class="keyword-movements__loader-container" v-else>
      <div class="keyword-movements__loader">
        <base-loader/>
      </div>
    </div>
    <button class="keyword-movements__button keyword-movements__button_close"
            @click.prevent="close">
      Close
    </button>

  </div>
</template>

<script>
/* eslint-disable no-unused-vars */
import BaseModalMixins from '@/common/BaseModalMixins'
import { mapActions, mapState } from 'vuex'
// table config similar to RankZone table config
import { topLevelConfig, secondLevelConfig, tableRows } from './Helper'
import Reporting from './Reporting'
import RankZone from './RankZone'
import filters from '@/common/Filters'
import Progress from '@/features/reports/rank-zone/progress/Progress'
import _ from 'lodash'
import csvExport from '@/services/CSVExport'
import moment from 'moment'
/**
 * Modal shows keyword movement
 * Used in {@link Reporting} tab
 * Invoked from {@link RankZone} component
 */
export default {
  name: 'KeywordMovementTable',
  mixins: [BaseModalMixins],
  props: {
    item: {
      type: Object,
      required: true
    },
    cellName: {
      type: String,
      required: true
    },
    clickedItem: {
      type: Object,
      required: true
    },
    /**
     * define is component used in {@link Progress} tab
     */
    isReport: {
      type: Boolean,
      default: false
    },
    // if true show snapshot data for selected snapshot
    showSnapshotData: {
      type: Boolean,
      default: false
    },
    // if true show snapshot data without ND for selected snapshot
    isBaseLine: {
      type: Boolean,
      default: false
    },
    // used in Progress tab to get right data
    webPropertyGroupId: {
      type: String,
      default: null
    },
    // advanced filters from Progress tab
    advancedFilters: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isDataFetched: false,
      config: [
        topLevelConfig,
        secondLevelConfig
      ],
      tableRows
    }
  },
  created () {
    this.getKeywords()
    // add search in google logic
    const configLevel = { ...this.config[1] }
    configLevel.cells[0].searchInGoogle = true
    configLevel.showHistory = true
    configLevel.clickable = true
    this.config[1] = configLevel
  },
  beforeDestroy () {
    this.resetKeywordsMovement()
  },
  watch: {
    selectedClient (client) {
      if (!client) {
        return
      }
      this.close()
    }
  },
  filters: {
    commaDividedNumber: filters.commaDividedNumber
  },
  computed: {
    ...mapState('briefContent', [
      'briefContent'
    ]),
    ...mapState('rankZoneKeywordMovement', {
      tableData: 'tableData',
      total: 'total',
      keywords: 'keywords'
    }),
    snapshots () {
      return this.briefContent.snapshots
    },
    keywordMovementTitle () {
      let dateFrom = new Date(this.clickedItem.snapshot_date_from)
      let dateTo = new Date(this.clickedItem.snapshot_date_to)
      const dateOptions = { month: 'long', day: 'numeric', year: 'numeric' }

      if (this.showSnapshotData) {
        return `${dateFrom.toLocaleDateString('en-EN', dateOptions).replace(/,/, '')} Snapshot`
      }

      return `${dateFrom.toLocaleDateString('en-EN', dateOptions).replace(/,/, '')} movement to ${dateTo.toLocaleDateString('en-EN', dateOptions).replace(/,/, '')}`
    }
  },
  methods: {
    ...mapActions('rankZoneKeywordMovement', ['getKeywordsMovement', 'resetKeywordsMovement',
      'getKeywordsMovementProgress', 'getSnapshot']),
    async getKeywords () {
      if (this.isReport) {
        if (this.showSnapshotData) {
          await this.getSnapshot(
            {
              clientId: this.clientId,
              snapshot_date: this.item.snapshot_date_from,
              web_property_group_id: this.webPropertyGroupId,
              advancedFilters: this.advancedFilters,
              isBaseLine: this.isBaseLine
            }
          )
        } else {
          await this.getKeywordsMovementProgress(
            {
              clientId: this.clientId,
              rank_zone: this.item.rank_zone.replace(' to ', '_'),
              snapshot_date_from: this.item.snapshot_date_from,
              snapshot_date_to: this.item.snapshot_date_to,
              web_property_group_id: this.webPropertyGroupId,
              advancedFilters: this.advancedFilters
            }
          )
        }
      } else {
        await this.getKeywordsMovement(
          {
            clientId: this.clientId,
            rank_zone: this.item.rank_zone.replace(' to ', '_'),
            snapshot_date_from: this.item.snapshot_date_from,
            snapshot_date_to: this.item.snapshot_date_to,
            web_property_group_id: this.webPropertyGroupId,
            url_id: this.briefContent.url_id
          }
        )
      }
      this.isDataFetched = true
    },
    close () {
      this.resetKeywordsMovement()
      this.$emit('close')
    },
    exportCsv () {
      const getRankZone = (rank) => {
        switch (true) {
          case rank <= 3:
            return '01 to 03'
          case rank <= 7:
            return '04 to 07'
          case rank <= 10:
            return '08 to 10'
          case rank <= 25:
            return '11 to 25'
          case rank <= 50:
            return '26 to 50'
          case rank <= 100:
            return '51 to 100'
          default:
            return 'NR'
        }
      }
      const handler = (columns) => {
        return item => {
          const row = []
          columns.forEach(name => {
            if (name === 'baseline_rank_zone') {
              row.push(`"${getRankZone(item.baseline_rank)}"`)
            } else if (name === 'comparison_rank_zone') {
              row.push(`"${getRankZone(item.comparison_rank)}"`)
            } else if (name === 'content_brief_url') {
              row.push(`"${window.location.origin + item.content_brief_url}"`)
            } else if (['comparison_url', 'baseline_url'].includes(name)) {
              row.push(`"${item[name] ? item[name] : 'NR'}"`)
            } else {
              row.push(`"${item[name]}"`)
            }
          })
          data.push(row)
        }
      }

      let header = ['"Keyword"', '"Relevancy Score"', '"Monthly Searches"', '"Baseline Rank Date"',
        '"Baseline Rank Zone"', '"Baseline Rank"', '"Comparison Rank Date"', '"Comparison Rank Zone"',
        '"Comparison Rank"', '"Comparison Ranking URL"', '"Mapped URL"', '"Mapped URL Content Brief"']
      let columnNames = ['label', 'keyword_score', 'total_msv', 'baseline_date',
        'baseline_rank_zone', 'baseline_rank', 'comparison_date', 'comparison_rank_zone',
        'comparison_rank', 'comparison_url', 'mapped_url', 'content_brief_url']

      if (this.showSnapshotData) {
        header = ['"Keyword"', '"Relevancy Score"', '"Monthly Searches"', '"Rank Date"',
          '"Rank Zone"', '"Rank"', '"Ranking URL"', '"Mapped URL"', '"Mapped URL Content Brief"']
        columnNames = ['label', 'keyword_score', 'total_msv', 'baseline_date',
          'baseline_rank_zone', 'baseline_rank', 'baseline_url', 'mapped_url', 'content_brief_url']
      }

      const data = []
      data.push(header)
      // sort by rank
      const keywords = _.cloneDeep(this.keywords).sort((a, b) => (a.baseline_rank > b.baseline_rank) ? 1 : ((b.baseline_rank > a.baseline_rank) ? -1 : 0))
      keywords.forEach(handler(columnNames))
      csvExport(`${this.selectedClient.name.replace(/ /g, '-')}_${this.keywordMovementTitle.replace(/ - /g, '-').replace(/ /g, '-')}`, data)
    }
  }
}
</script>

<style scoped lang="scss">
  .keyword-movements {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &__header {
      display: flex;
      padding: 40px;
    }

    &::v-deep .common-table,
    &::v-deep .common-table__header-cell {
      border-radius: 0 !important;
    }

    &::v-deep .base-collapsible-table-cell__second-level {
      .base-collapsible-table-cell__title {
        padding-left: 0 !important;
      }
    }

    &::v-deep .base-collapsible-table-cell__top-level {
      .base-collapsible-table-cell__arrow {
        right: 827px !important;
      }

      .base-collapsible-table-cell__title {
        padding-left: 28px !important;
      }
    }

    &__rank-zone,
    &__title {
      text-align: center;
      font-size: 24px;
      line-height: 24px;
      color: $rfx-dark-march;
      letter-spacing: -0.24px;
    }

    &__title {
      font-weight: bold;
      margin-right: 40px;
    }

    &__export-button {
      position: absolute;
      top: 43px;
      right: 30px;
      font-size: 18px;
      color: $rfx-bright-blue;
    }

    &__footer {
      height: 60px;
      background-color: $rfx-border-color;
      font-weight: bold;
      color: $rfx-dark-march;
      display: flex;
      align-items: center;

      &-title {
        flex-basis: 71%;
        padding-left: 40px;
      }

      &-msv {
        flex-basis: 13%;
      }

      &-keywords {
        flex-basis: 13%;
      }
    }

    &__button {
      width: 100px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      border: none;
      outline: none;
      align-self: flex-end;
      margin: 20px;
      cursor: pointer;

      &_close {
        background-color: $rfx-medium-march;
        color: #fff;
      }
    }

    &__loader {
      display: flex;
      justify-content: center;
      align-items: center;
      height: calc(100vh - 530px)
    }

    &__loader-container {

    }
  }
</style>
